import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Calendar,
  Clock,
  User,
  Mail,
  FileText,
  CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Scheduling() {
  const [appointments, setAppointments] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [apptRes, availRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/availability`)
      ]);
      setAppointments(apptRes.data);
      setAvailability(availRes.data);
    } catch (error) {
      console.error("Failed to fetch scheduling data:", error);
    } finally {
      setLoading(false);
    }
  };

  const groupedAvailability = availability.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const upcomingAppointments = appointments.filter(
    a => new Date(a.appointment_datetime) >= new Date()
  );

  const pastAppointments = appointments.filter(
    a => new Date(a.appointment_datetime) < new Date()
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="scheduling-page">
      <div className="page-header">
        <h1 className="page-title">Scheduling</h1>
        <p className="page-subtitle">Manage appointments and availability</p>
      </div>
      
      <div className="page-content">
        <Tabs defaultValue="appointments" className="space-y-6">
          <TabsList>
            <TabsTrigger value="appointments" data-testid="appointments-tab">
              Appointments ({appointments.length})
            </TabsTrigger>
            <TabsTrigger value="availability" data-testid="availability-tab">
              Availability
            </TabsTrigger>
          </TabsList>

          <TabsContent value="appointments" className="space-y-6">
            {/* Upcoming Appointments */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Calendar size={18} className="text-accent" />
                  Upcoming Appointments ({upcomingAppointments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingAppointments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No upcoming appointments</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upcomingAppointments.map((appt) => (
                      <div 
                        key={appt.appointment_id}
                        className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors"
                        data-testid={`appointment-${appt.appointment_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Clock size={16} className="text-muted-foreground" />
                              <span className="font-medium">
                                {new Date(appt.appointment_datetime).toLocaleString()}
                              </span>
                              <Badge className="status-success">{appt.status}</Badge>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <User size={14} />
                              <span>{appt.name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Mail size={14} />
                              <span>{appt.email}</span>
                            </div>
                            {appt.notes && (
                              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                                <FileText size={14} className="mt-0.5" />
                                <span>{appt.notes}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-right text-sm text-muted-foreground">
                            <p>Lead ID:</p>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {appt.lead_id.slice(0, 8)}...
                            </code>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Past Appointments */}
            {pastAppointments.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-muted-foreground">
                    Past Appointments ({pastAppointments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pastAppointments.map((appt) => (
                      <div 
                        key={appt.appointment_id}
                        className="flex items-center justify-between p-3 border border-border rounded-lg opacity-70"
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle size={16} className="text-green-500" />
                          <div>
                            <p className="text-sm font-medium">{appt.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(appt.appointment_datetime).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <Badge variant="secondary">{appt.status}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="availability" className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Available Time Slots</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-6">
                  The following time slots are available for booking. Leads can select these when scheduling a call.
                </p>
                <div className="space-y-6">
                  {Object.entries(groupedAvailability).map(([date, slots]) => (
                    <div key={date}>
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <Calendar size={16} className="text-muted-foreground" />
                        {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </h3>
                      <div className="time-slots-grid">
                        {slots.map((slot) => (
                          <div 
                            key={slot.slot_id}
                            className="time-slot"
                          >
                            <span className="text-sm font-medium">
                              {slot.start_time} - {slot.end_time}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
